import { type FC } from 'react';
import { defineMessages, useIntl, type MessageDescriptor } from 'dibs-react-intl';
import { type InitiateConnectionResult } from 'dibs-crypto-utils/exports/useWeb3Connection';
import { Button, type ButtonProps } from 'dibs-elements/exports/Button';
import { Spinner } from 'dibs-elements/exports/Spinner';
import MetaMaskIcon from 'dibs-icons/exports/legacy/MetaMask';

import styles from './ConnectMetaMaskButton.scss';

const messages = defineMessages({
    connectMetaMask: {
        id: 'connectMetaMask.cta.connectMetaMask',
        defaultMessage: 'metamask',
    },
    openMetaMask: {
        id: 'connectMetaMask.cta.openMetaMask',
        defaultMessage: 'Open metamask',
    },
    connecting: {
        id: 'connectMetaMask.cta.connecting',
        defaultMessage: 'Connecting',
    },
});

const metaMaskDownloadUrl = 'https://metamask.io/download';

function getCtaMessage(useOpenCopy: boolean, connecting?: boolean): MessageDescriptor {
    if (useOpenCopy) {
        return messages.openMetaMask;
    } else if (connecting) {
        return messages.connecting;
    } else {
        return messages.connectMetaMask;
    }
}

type Props = {
    metaMaskNotInstalled: boolean;
    fullWidth?: boolean;
    useOpenCopy?: boolean;
    connected?: InitiateConnectionResult;
    onConnect?: () => Promise<void> | void;
    size?: ButtonProps['size'];
    disabled?: boolean;
    onInstallClick?: () => void;
    disableDownloadLink?: boolean;
};

export const ConnectMetaMaskButton: FC<Props> = ({
    connected = {},
    onConnect,
    metaMaskNotInstalled,
    fullWidth,
    size,
    useOpenCopy = false,
    disabled = false,
    onInstallClick = () => {},
    disableDownloadLink = false,
}) => {
    const intl = useIntl();

    const connectToMetaMask = async (): Promise<void> => {
        if (connected.connecting) {
            return;
        }
        if (onConnect) {
            await onConnect();
        }
    };

    const ctaMessage = getCtaMessage(useOpenCopy, connected.mmConnecting);

    return (
        <Button
            size={size}
            dataTn="connect-metamask-cta"
            onClick={metaMaskNotInstalled ? onInstallClick : () => connectToMetaMask()}
            disabled={connected.connecting || disabled}
            href={metaMaskNotInstalled && !disableDownloadLink ? metaMaskDownloadUrl : undefined}
            opensInNewWindow={metaMaskNotInstalled ? true : undefined}
            fullWidth={fullWidth}
            type={connected.connecting || disabled ? 'primary' : 'transparent'}
        >
            <div className={styles.buttonContent}>
                <div className={styles.iconContainer}>
                    <MetaMaskIcon />
                </div>
                {intl.formatMessage(ctaMessage)}
                {connected.mmConnecting && (
                    <Spinner size="tiny" black containerClass={styles.spinnerContainer} />
                )}
            </div>
        </Button>
    );
};
